import type {
  ManifestConditionalItem,
  ManifestConditionalPackage,
  ManifestConditionalValue,
  ResolvedManifest,
  RawManifest,
} from './types';

export class ManifestResolver {
  constructor(
    private readonly config: {
      casinoId: string;
    }
  ) {}

  resolve(manifest: RawManifest): ResolvedManifest {
    if (!this.isValidManifest(manifest)) {
      throw new Error('Invalid manifest');
    }

    const result: ResolvedManifest = {
      flags: {},
      packages: {},
      values: {},
      raw: manifest,
    };

    for (const [key, value] of Object.entries(manifest)) {
      const resolvedItem = this.resolveItem(value);

      // if resolved item is boolean - add it to flags, and continue to next one
      if (typeof resolvedItem === 'boolean') {
        result.flags[key] = resolvedItem;
        continue;
      }

      // if resolved item has a version - add it to the packages, and continue to next one
      if (resolvedItem?.version !== undefined) {
        result.packages[key] = resolvedItem;
        continue;
      }
      // if resolved item has a value - add it to the values, and continue to next one
      if (resolvedItem?.value !== undefined) {
        result.values[key] = resolvedItem;
        continue;
      }

      // if there is no resolved value - keep the original value
      // if there is a resolved value - keep the resolved value
      result[key] = resolvedItem === undefined ? value : resolvedItem;
    }

    return result;
  }

  private isValidManifest(manifest: unknown): manifest is RawManifest {
    return Object.prototype.toString.call(manifest) === '[object Object]';
  }

  resolveCondition(item: ManifestConditionalItem[]): ManifestConditionalItem | undefined {
    return item.find((item) => item.condition?.casinoIds?.includes(this.config.casinoId) || !item.condition);
  }

  resolveItem(item: ManifestConditionalItem[] | boolean): ManifestConditionalItem | boolean | undefined {
    if (typeof item === 'boolean') return item;

    if (Array.isArray(item)) {
      const resolvedItem = this.resolveCondition(item);

      if (typeof resolvedItem === 'object') {
        if ('enabled' in resolvedItem) {
          return Boolean(resolvedItem.enabled);
        }
        if ('value' in resolvedItem) {
          return resolvedItem as ManifestConditionalValue;
        }
        if ('version' in resolvedItem) {
          return resolvedItem as ManifestConditionalPackage;
        }
      }
      return resolvedItem;
    }
  }

  static merge(...manifests: ResolvedManifest[]): ResolvedManifest {
    const mergedManifest: ResolvedManifest = {
      flags: {},
      packages: {},
      values: {},
      raw: {},
    };

    manifests.forEach((manifest) => {
      Object.assign(mergedManifest.flags, manifest.flags);
      Object.assign(mergedManifest.packages, manifest.packages);
      Object.assign(mergedManifest.values, manifest.values);
      Object.assign(mergedManifest.raw, manifest.raw);
    });

    return mergedManifest;
  }
}
